import { useState } from "react";
import { MicroLabel } from "../primitives/MicroLabel.js";
import { ToolModal } from "../primitives/ToolModal.js";

export type SkillRadarEntry = {
  skillFamily: string;
  /** Mastery score 0..1 */
  mastery: number;
  /** Optional median snap latency in ms for the inline annotation */
  medianSnapMs?: number;
};

export type SkillRadarProps = {
  entries: SkillRadarEntry[];
  /** SVG render size (square). The container is wider to fit labels. */
  size?: number;
  /** Pretty label override for skill_family names. */
  formatLabel?: (skill: string) => string;
};

// Above this many axes the ring labels can't fit without colliding, so the
// inline chart drops them and becomes a tap target for the full-screen view.
const DENSE_THRESHOLD = 12;

/**
 * SkillRadar — radar chart of mastery across skill families.
 *
 * With many skills (the mastery map can carry 30-40) the perimeter labels
 * collide into an unreadable pile. So past DENSE_THRESHOLD axes the inline
 * chart renders the shape only (no labels) and the whole card becomes a button:
 * tapping opens a full-screen modal with a larger chart AND a readable, sorted
 * list of every skill + its mastery — which never overlaps and works on mobile
 * (the modal rises as a bottom sheet). Few-axis radars keep their labels.
 */
export function SkillRadar({ entries, size = 220, formatLabel }: SkillRadarProps) {
  const [open, setOpen] = useState(false);

  if (entries.length < 3) {
    return (
      <div className="bht-skill-radar bht-skill-radar--empty">
        <MicroLabel>Skill map opens after 3 skills</MicroLabel>
      </div>
    );
  }

  const dense = entries.length > DENSE_THRESHOLD;
  const label = (e: SkillRadarEntry) => formatLabel?.(e.skillFamily) ?? e.skillFamily;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Expand skill map"
        style={{
          appearance: "none",
          background: "none",
          border: "none",
          padding: 0,
          width: "100%",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
        }}
      >
        <RadarSvg entries={entries} size={size} showLabels={!dense} formatLabel={formatLabel} />
        <MicroLabel>Tap to expand{dense ? ` · ${entries.length} skills` : ""}</MicroLabel>
      </button>

      {open && (
        <ToolModal title="Your skill map" onClose={() => setOpen(false)} size="wide">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ alignSelf: "center", width: "100%", maxWidth: 380 }}>
              {/* Large chart; labels still off when dense — the list below is the
                  readable key, so the chart stays an uncluttered shape. */}
              <RadarSvg entries={entries} size={dense ? 300 : 260} showLabels={!dense} formatLabel={formatLabel} />
            </div>
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: "42vh",
                overflowY: "auto",
              }}
            >
              {[...entries]
                .sort((a, b) => a.mastery - b.mastery)
                .map((e) => {
                  const pct = Math.round(Math.max(0, Math.min(1, e.mastery)) * 100);
                  return (
                    <li
                      key={e.skillFamily}
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13,
                          color: "var(--fg-1)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {label(e)}
                      </span>
                      <span
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          width: 84,
                          height: 6,
                          borderRadius: 3,
                          background: "var(--border-faint)",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            width: `${pct}%`,
                            height: "100%",
                            background:
                              pct >= 80
                                ? "var(--signal-green)"
                                : pct <= 25
                                ? "var(--accent-amber)"
                                : "var(--fg-2)",
                          }}
                        />
                      </span>
                      <span
                        style={{
                          flexShrink: 0,
                          width: 38,
                          textAlign: "right",
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          color: "var(--fg-1)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {pct}%
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>
        </ToolModal>
      )}
    </>
  );
}

/** The radar SVG itself — shape + rings + optional perimeter labels. */
function RadarSvg({
  entries,
  size,
  showLabels,
  formatLabel,
}: {
  entries: SkillRadarEntry[];
  size: number;
  showLabels: boolean;
  formatLabel?: (skill: string) => string;
}) {
  const padX = showLabels ? 92 : 12;
  const padY = showLabels ? 36 : 12;
  const containerW = size + padX * 2;
  const containerH = size + padY * 2;

  const cx = size / 2;
  const cy = size / 2;
  const R = size / 2 - 8;
  const N = entries.length;
  const rings = [0.25, 0.5, 0.75, 1.0];

  const polygonPoints = entries
    .map((e, i) => {
      const theta = (i / N) * Math.PI * 2 - Math.PI / 2;
      const r = Math.max(0, Math.min(1, e.mastery)) * R;
      return [cx + Math.cos(theta) * r, cy + Math.sin(theta) * r];
    })
    .map(([x, y]) => `${x?.toFixed(1)},${y?.toFixed(1)}`)
    .join(" ");

  const labelOffset = 14;
  const labels = showLabels
    ? entries.map((e, i) => {
        const theta = (i / N) * Math.PI * 2 - Math.PI / 2;
        const lx = cx + Math.cos(theta) * (R + labelOffset);
        const ly = cy + Math.sin(theta) * (R + labelOffset);
        const cos = Math.cos(theta);
        let align: "left" | "right" | "center";
        let translateX: string;
        if (Math.abs(cos) < 0.25) {
          align = "center";
          translateX = "translateX(-50%)";
        } else if (cos > 0) {
          align = "left";
          translateX = "translateX(0)";
        } else {
          align = "right";
          translateX = "translateX(-100%)";
        }
        return {
          key: e.skillFamily,
          text: formatLabel?.(e.skillFamily) ?? e.skillFamily,
          x: lx + padX,
          y: ly + padY,
          align,
          translateX,
        };
      })
    : [];

  return (
    <div
      className="bht-skill-radar"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: containerW,
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <svg width="100%" viewBox={`0 0 ${containerW} ${containerH}`} role="img" aria-label="Skill mastery radar">
        <g transform={`translate(${padX} ${padY})`}>
          {rings.map((frac) => (
            <circle key={frac} cx={cx} cy={cy} r={frac * R} className="bht-skill-radar__ring" fill="none" />
          ))}
          {entries.map((_, i) => {
            const theta = (i / N) * Math.PI * 2 - Math.PI / 2;
            return (
              <line
                key={i}
                x1={cx}
                y1={cy}
                x2={cx + Math.cos(theta) * R}
                y2={cy + Math.sin(theta) * R}
                className="bht-skill-radar__axis"
              />
            );
          })}
          <polygon points={polygonPoints} className="bht-skill-radar__polygon" />
        </g>
      </svg>
      {labels.map((l) => (
        <div
          key={l.key}
          style={{
            position: "absolute",
            left: `calc(${(l.x / containerW) * 100}% )`,
            top: `calc(${(l.y / containerH) * 100}% )`,
            transform: `${l.translateX} translateY(-50%)`,
            maxWidth: 88,
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            lineHeight: 1.25,
            fontWeight: 500,
            color: "var(--fg-1)",
            textAlign: l.align,
            pointerEvents: "none",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {l.text}
        </div>
      ))}
    </div>
  );
}
